import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import StatusBadge from "../UI_Kit/StatusBadge/StatusBadge";
import "./DataTable.css";
import api from "../../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmationModal from "../Modals/ConfirmationModal"; // Adjust path if needed

// Define the valid statuses from the API
const validStatuses = [
  "draft",
  "pending",
  "validated",
  "sent",
  "to_review",
  "won",
  "lost",
  "archived",
];

// French translations mapping
const statusTranslations = {
  draft: "Brouillon",
  pending: "En attente",
  validated: "Validé",
  to_review: "À réviser",
  sent: "Envoyé",
  won: "Gagné",
  lost: "Perdu", 
  archived: "Archivé",
};

// Helper to translate status keys to French
const translateStatusToFrench = (status) => {
  return statusTranslations[status.toLowerCase()] || status; // Fallback to original key if not found
};

const TableComponent = ({
  data,
  onDataChange,
  currentPage,
  totalPages,
  // perPage, // perPage from props might not be directly used by react-table if totalPages is authoritative
  onPageChange,
  loading: homeLoading, // Renaming to avoid conflict if DataTable has its own loading state
}) => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState(data);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filters, setFilters] = useState({
    clientName: "",
    dateFrom: "",
    dateTo: "",
    status: [],
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [quotationToArchive, setQuotationToArchive] = useState(null); // Renamed from quotationToDelete
  const [isArchiving, setIsArchiving] = useState(false); // Renamed from isDeleting
  const [updatingRowId, setUpdatingRowId] = useState(null); // State to track which row is updating
  const [showMenu, setShowMenu] = useState(null);

  const handleShowArchiveConfirm = (rowData) => {
    // Renamed from handleShowDeleteConfirm
    setQuotationToArchive(rowData); // Store the row data
    setShowConfirmModal(true); // Show the modal
  };

  const handleCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
    setQuotationToArchive(null); // Clear the stored data
  }, []); // Dependencies: setShowConfirmModal and setQuotationToArchive are stable from useState

  // Renamed and modified to update status to 'archived'
  const confirmArchive = useCallback(async () => {
    if (!quotationToArchive) return; // Safety check
    setIsArchiving(true); // Set archiving state to true
    const quotationId = quotationToArchive.quotationId; // Use the stored ID

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("Authentication token not found.");
        setIsArchiving(false); // Reset archiving state on early return
        handleCloseConfirmModal();
        return;
      }

      // The API call archives the quotation (e.g., sets a flag or moves it)
      // await api.patch(`/quotation/${quotationId}/status`, { status: 'archived' }, ...);
      await api.delete(`/quotation/${quotationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(`Devis ${quotationToArchive.id} archivé avec succès.`);

      // Update local state to REMOVE the archived item
      setTableData((prevData) => {
        const updatedData = prevData.filter(
          (item) => item.quotationId !== quotationId
        );
        // Notify parent if data changed
        if (onDataChange) {
          onDataChange(updatedData);
        }
        return updatedData;
      });
    } catch (error) {
      console.error("Failed to archive quotation:", error);
      let errorMsg = "Failed to archive quotation.";
      if (error.response?.data?.message) {
        errorMsg += ` ${error.response.data.message}`;
      }
      toast.error(errorMsg);
    } finally {
      setIsArchiving(false); // Reset archiving state
      handleCloseConfirmModal(); // Ensure modal closes even on error
    }
  }, [quotationToArchive, onDataChange, handleCloseConfirmModal]); // Added handleCloseConfirmModal to dependencies

  // Update local state when prop changes
  React.useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleView = useCallback(
    (rowData) => {
      navigate(`/invoice/create/step1/${rowData.quotationId}`);
    },
    [navigate]
  );

  const handleViewIdClick = useCallback(
    (rowData) => {
      navigate(`/invoice/create/step3/${rowData.quotationId}`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (rowData) => {
      navigate(`/invoice/create/step3/${rowData.quotationId}`);
    },
    [navigate]
  );

  const handleStatusChange = useCallback(
    async (rowId, newStatus) => {
      // Ensure the status is one of the valid keys
      if (!validStatuses.includes(newStatus)) {
        console.error(`Invalid status update attempted: ${newStatus}`);
        toast.error(`Invalid status: ${newStatus}`);
        return;
      }

      setUpdatingRowId(rowId); // Set the updating row ID
      try {
        console.log("RowId:", rowId);
        console.log("NewStatus:", newStatus);
        // Call the API to update the status
        await api.patch(
          `/quotation/${rowId}/status`,
          {
            status: newStatus, // Pass the already validated newStatus
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        // Update local state if the API call is successful
        setTableData((prevData) => {
          const updatedData = prevData.map((item) => {
            // Use quotationId for matching
            if (item.quotationId === rowId && item.status !== newStatus) {
              // Update status and use the translation function for display
              return {
                ...item,
                status: newStatus,
                // Remove translated_status if it's no longer needed from API
                // translated_status: translateStatusToFrench(newStatus) // Or keep if needed elsewhere
              };
            }
            return item;
          });

          // Notify parent if data changed
          if (
            JSON.stringify(prevData) !== JSON.stringify(updatedData) &&
            onDataChange
          ) {
            onDataChange(updatedData);
          }

          return updatedData;
        });
        toast.success(`Statut mis à jour avec succès pour le devis ${rowId}.`); // Add success toast
      } catch (error) {
        console.error("Failed to update status:", error);
        let errorMsg = "Failed to update status.";
        if (error.response?.data?.message) {
          errorMsg += ` ${error.response.data.message}`;
        }
        toast.error(errorMsg); // Add error toast
      } finally {
        setUpdatingRowId(null); // Reset the updating row ID regardless of success or failure
      }
    },
    [onDataChange]
  );

  const handleMultiStatusFilterChange = (statusValue) => {
    setFilters((prev) => {
      const newStatus = prev.status.includes(statusValue)
        ? prev.status.filter((s) => s !== statusValue) // Remove if already selected
        : [...prev.status, statusValue]; // Add if not selected
      return { ...prev, status: newStatus };
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    setShowFilterSidebar(false);
  };

  const resetFilters = () => {
    setFilters({
      clientName: "",
      dateFrom: "",
      dateTo: "",
      status: [],
    });
  };

  const handleDownload = (url) => {
    console.log("url : " , url)
    if (url) {
      window.open(url, '_blank');
    } else {
      console.error("Download URL is not available.");
      toast.error("Le lien de téléchargement n'est pas disponible.");
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Numéro de devis",
        cell: ({ row }) => (
          <span
            onClick={() => handleViewIdClick(row.original)}
            style={{ cursor: "pointer" , textDecoration:"underline"}}
          >
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "État d'avancement",
        cell: ({ row }) => {
          // Get the French translation for display
          const frenchStatus = translateStatusToFrench(row.original.status);
          return (
            <StatusBadge
              status={row.original.status} // Keep passing the status key
              displayStatus={frenchStatus} // Pass the French translation for display
              onStatusChange={handleStatusChange}
              rowId={row.original.quotationId}
              isUpdating={updatingRowId === row.original.quotationId}
              translateStatus={translateStatusToFrench} // Pass the French translation function
            />
          );
        },
      },
      { accessorKey: "company", header: "Entreprise" },
      { accessorKey: "creationDate", header: "Date de creation" },
      { accessorKey: "devisNumber", header: "Numéro devis" },
      { accessorKey: "totalTTC", header: "Total TTC" },
      { accessorKey: "type", header: "Type" },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => (
          <>
            <div className="datatable-actions">
              <span
                className="datatable-options-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(showMenu === row.id ? null : row.id);
                  console.log('Row ID:', row.id);
                }}
              >
                ⋮
              </span>
              {showMenu === row.id && (
                <div className="datatable-custom-dropdown-menu">
                  <div className="datatable-dropdown-arrow" />
                  {(row.original.status === 'Draft' || row.original.status === 'To_review') && (
                    <button className="datatable-custom-dropdown-item"  onClick={() => handleView(row.original)}>
                      <img src="/edit_alt.png" className="action-icon" alt="edit"></img>{" "}
                      Modifier
                    </button>
                  )}
                  <button className="datatable-custom-dropdown-item" onClick={() => handleDownload(row.original.pfd_link)}>
                    <img src="/Download.png" className="action-icon" alt="download"></img>{" "}
                    Télécharger
                  </button>
                  <button className="datatable-custom-dropdown-item"  onClick={() => handleShowArchiveConfirm(row.original)} >
                    <img src="/Folder-open.png" className="action-icon" alt="archiver"></img>{" "}
                    Archiver
                  </button>
                </div>
              )}
            </div>
          </>
        ),
      },
    ],
    // Add handleShowArchiveConfirm and remove handleShowDeleteConfirm
    [handleStatusChange, updatingRowId, handleEdit, handleView, showMenu, handleDownload] // Add handleDownload to dependency array
  );

  const [sorting, setSorting] = useState([]);
  // Remove local pagination state: const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Filter the data based on search term and filters
  const filteredData = useMemo(() => {
    // Server-side pagination means we don't filter data here anymore based on pagination
    // Filtering logic for search, clientName, date range, status can remain if they are client-side filters
    // If these filters are also server-side, this filtering logic should be removed or adjusted.
    // For now, assuming these are client-side filters on the current page's data.
    return tableData.filter((item) => {
      // Search term filter (searches across all fields)
      const searchMatch =
        searchTerm === "" ||
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        );

      // Client name filter
      const clientMatch =
        filters.clientName === "" ||
        item.company.toLowerCase().includes(filters.clientName.toLowerCase());

      // Date range filter
      let dateMatch = true;
      if (filters.dateFrom || filters.dateTo) {
        const itemDate = new Date(item.creationDate);
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const toDate = filters.dateTo ? new Date(filters.dateTo) : null;

        if (fromDate && itemDate < fromDate) dateMatch = false;
        if (toDate && itemDate > toDate) dateMatch = false;
      }

      // Status filter - modified to check if array is empty or contains the item's status
      const statusMatch =
        filters.status.length === 0 ||
        filters.status.includes(item.status?.toLowerCase());

      return searchMatch && clientMatch && dateMatch && statusMatch;
    });
  }, [tableData, searchTerm, filters]);

  const table = useReactTable({
    data: filteredData, // Or `data` directly if no client-side filtering on the current page data
    columns,
    state: {
      sorting,
      // pagination is now controlled manually
      pagination: {
        pageIndex: currentPage - 1, // react-table is 0-indexed
        pageSize: data.length, // pageSize could be derived from data length or passed as prop if fixed
      },
    },
    pageCount: totalPages, // Set pageCount for react-table
    manualPagination: true, // Enable manual pagination
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(), // Not needed for manual pagination
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(), // Keep if client-side filtering is used
    onSortingChange: setSorting,
    // onPaginationChange: setPagination, // Not needed for manual pagination
  });

  // if (homeLoading) { // Optionally show a loader within the table itself
  //   return <div className="loading-container">Chargement des données...</div>;
  // }

  if (!data || (data.length === 0 && !homeLoading)) {
    return <div className="empty-state">Aucune donnée disponible</div>;
  }

  return (
    <div className="custom-table-wrapper">
      <ToastContainer />
      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        <div className="search-input-container">
          <div className="search-icon-container">
            <img src="/Search.png" alt="Search" className="search-icon" />
          </div>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
        <span
          className="filter-button"
          onClick={() => setShowFilterSidebar(true)}
          aria-label="Filter"
        >
          <img src="/filtre.png" alt="Filter" className="filter-icon" />
        </span>
      </div>

      <div className="custom-table-container mt-4 mb-4">
        <table className="custom-table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: "pointer" }}
                    aria-sort={
                      header.column.getIsSorted() === "asc"
                        ? "ascending"
                        : header.column.getIsSorted() === "desc"
                        ? "descending"
                        : "none"
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() === "asc" ? (
                      <img
                        src="/Caret-up.png"
                        alt="Sort ascending"
                        width="15px"
                      />
                    ) : header.column.getIsSorted() === "desc" ? (
                      <img
                        src="/Caret-down.png"
                        alt="Sort descending"
                        width="15px"
                      />
                    ) : (
                      ""
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="pagination-container">
        <button
          className="pagination-button"
          onClick={() => onPageChange(1)} // Use onPageChange
          disabled={currentPage === 1 || homeLoading}
          aria-label="First page"
        >
          «
        </button>
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage - 1)} // Use onPageChange
          disabled={currentPage === 1 || homeLoading}
          aria-label="Previous page"
        >
          <img
            src="/arrow-left.png"
            alt="Previous"
            className="pagination-arrow"
          />
        </button>

        {/* Dynamic page numbers - adjust as needed for better UX with many pages */}
        {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
          const pageNum =
            totalPages > 5
              ? Math.min(
                  Math.max(0, currentPage - 1 - 2) + i, // currentPage is 1-indexed
                  totalPages - 1
                )
              : i;
          const displayPageNum = pageNum + 1;
          return (
            <button
              key={displayPageNum}
              className={`pagination-button ${
                displayPageNum === currentPage ? "active" : ""
              }`}
              onClick={() => onPageChange(displayPageNum)} // Use onPageChange
              disabled={homeLoading}
              aria-current={displayPageNum === currentPage ? "page" : undefined}
            >
              {displayPageNum}
            </button>
          );
        })}

        {totalPages > 5 && currentPage < totalPages - 2 && <span>...</span>}

        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage + 1)} // Use onPageChange
          disabled={currentPage === totalPages || homeLoading}
          aria-label="Next page"
        >
          <img src="/arrow-right.png" alt="Next" className="pagination-arrow" />
        </button>
        <button
          className="pagination-button"
          onClick={() => onPageChange(totalPages)} // Use onPageChange
          disabled={currentPage === totalPages || homeLoading}
          aria-label="Last page"
        >
          »
        </button>
      </div>

      {/* Filter Sidebar */}
      <div className={`filter-sidebar ${showFilterSidebar ? "open" : ""}`}>
        <div className="filter-sidebar-header">
          <h3>Filtrer par</h3>
          <button
            className="close-sidebar-button"
            onClick={() => setShowFilterSidebar(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="filter-sidebar-content">
          <Form>
            <Form.Group className="mb-4">
              <Form.Label>Par client</Form.Label>
              <Form.Control
                type="text"
                name="clientName"
                value={filters.clientName}
                onChange={handleFilterChange}
                placeholder="Saisir le nom du client"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Par date de réalisation</Form.Label>
              <div className="date-input-wrapper d-flex align-items-center">
                <span className="me-2">Du</span>
                <Form.Control
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="custom-date-input"
                  style={{ color: "#333" }} // Add this line
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="date-input-wrapper d-flex align-items-center">
                <span className="me-2">Au</span>
                <Form.Control
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="custom-date-input"
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Par état d'avancement</Form.Label>
              {/* Use the validStatuses array and French translations for filter options */}
              {validStatuses.map((status) => (
                <div
                  className="checkbox-label custom-checkbox mb-3"
                  key={status}
                >
                  <input
                    type="checkbox"
                    value={status} // Use the lowercase status key
                    checked={filters.status.includes(status)} // Check against lowercase key
                    onChange={() => handleMultiStatusFilterChange(status)} // Pass lowercase key
                  />
                  {/* Display French translated status */}
                  <span className="filter-checkbox-label">
                    {translateStatusToFrench(status)}
                  </span>
                </div>
              ))}
            </Form.Group>
          </Form>
        </div>
        <div className="filter-sidebar-footer">
          <button className="secondary-button" onClick={resetFilters}>
            Réinitialiser
          </button>
          <button className="primary-button" onClick={applyFilters}>
            Appliquer
          </button>
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {showFilterSidebar && (
        <div
          className="sidebar-overlay"
          onClick={() => setShowFilterSidebar(false)}
        ></div>
      )}

      {/* Confirmation Modal for Archiving - CORRECTED PROPS */}
      <ConfirmationModal
        show={showConfirmModal}
        handleClose={handleCloseConfirmModal} // Use handleClose
        handleConfirm={confirmArchive}
        title="Confirmer l'archivage"
        isSubmitting={isArchiving}
        buttonTitle="Archiver"
        LinkTitle="Annuler"
      >
        {/* Use optional chaining for safety */}
        {/* CORRECTED: Use quotationToArchive */}
        {`Êtes-vous sûr de vouloir archiver le devis numéro ${quotationToArchive?.id} ?`}
      </ConfirmationModal>
    </div>
  );
};

TableComponent.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDataChange: PropTypes.func,
};

export default TableComponent;
